<template>
  <v-card class="sc">
    <header class="sc__head">
      <div class="sc__head-text">
        <p class="sc__eyebrow">Contabilidad</p>
        <h2 class="sc__title">Emitir estado de cuenta de {{ kindFrase }}</h2>
        <p class="sc__scope">{{ client.clientName }} · {{ periodLabel }}</p>
      </div>
      <button
        type="button"
        class="sc__close"
        title="Cerrar"
        @click="$emit('close-create')"
      >
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="sc__body">
      <!-- El periodo lo fija la pantalla (mes de la factura Alegra); aquí
           solo se decide el vencimiento. -->
      <div class="sc__inputs">
        <div class="sc__field sc__field--short">
          <label class="sc__label">Vence el</label>
          <input v-model="form.dueDate" type="date" class="sc__input" />
        </div>
      </div>
      <p class="sc__help">
        Entran los trámites con factura Alegra {{ alegraDe }}
        <template v-if="period"
          >fechada en {{ periodLabel }}</template
        >
        <template v-else
          >de cualquier mes</template
        >. Cada línea es esa factura: su número, su fecha y su monto. El N° de
        factura Alegra es obligatorio para emitir.
      </p>

      <!-- Lo que se va a cobrar -->
      <p v-if="loading" class="sc__help">Calculando…</p>
      <template v-else>
        <!-- Solo los conceptos del tipo: el otro estado de cuenta tiene los
             suyos. -->
        <div class="sc__totals">
          <template v-if="isReembolso">
            <div class="sc__total">
              <span class="sc__total-label">Impuestos adelantados</span>
              <strong class="sc__total-value">
                B/. {{ money(totals.TAX) }}
              </strong>
              <span class="sc__total-count">{{ counts.TAX }} trámites</span>
            </div>
            <div class="sc__total">
              <span class="sc__total-label">Otros gastos por su cuenta</span>
              <strong class="sc__total-value">
                B/. {{ money(totals.REIMBURSEMENT) }}
              </strong>
              <span class="sc__total-count">
                {{ counts.REIMBURSEMENT }} trámites
              </span>
              <span class="sc__total-hint">
                notaría, APA, bomberos, almacenaje…
              </span>
            </div>
          </template>
          <div v-else class="sc__total">
            <span class="sc__total-label">Servicios aduanales</span>
            <strong class="sc__total-value">
              B/. {{ money(totals.SERVICE) }}
            </strong>
            <span class="sc__total-count">{{ counts.SERVICE }} trámites</span>
          </div>
          <div class="sc__total sc__total--grand">
            <span class="sc__total-label">Total a facturar</span>
            <strong class="sc__total-value">B/. {{ money(total) }}</strong>
            <span class="sc__total-count">{{ tramites }} trámites</span>
          </div>
        </div>

        <!-- Con el N° y la fecha Alegra la tabla tiene ocho columnas: si no
             caben, se desplaza a lo ancho (barra a la vista) en vez de
             cortarse, y el Subtotal queda fijo a la derecha. -->
        <div class="sc__table-wrap">
          <table class="sc__table">
            <thead>
              <tr>
                <th class="sc__col-factura"># Factura</th>
                <th># Control</th>
                <th>N° Alegra</th>
                <th>Fecha Alegra</th>
                <th class="sc__right">CIF</th>
                <template v-if="isReembolso">
                  <th class="sc__right">Impuesto</th>
                  <th class="sc__right">Otros gastos</th>
                </template>
                <th v-else class="sc__right">Servicios</th>
                <th class="sc__right sc__sticky">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in porTramite" :key="row.invoiceId">
                <!-- "# Factura" son los números del cliente, uno por línea. El
                   consecutivo interno del consolidado va debajo como
                   referencia: el cliente no lo reconoce como factura. -->
                <td class="sc__strong sc__col-factura">
                  <div
                    v-for="(n, i) in row.clientInvoiceNumbers"
                    :key="i"
                    class="sc__factura"
                  >
                    {{ n }}
                  </div>
                  <div v-if="row.consolidated" class="sc__consolidado">
                    Consolidado {{ consolidadoDe(row) }} ·
                    {{ row.clientInvoiceNumbers.length }} facturas
                    <template v-if="controlFdDe(row)">
                      · Control F&amp;D: {{ controlFdDe(row) }}
                    </template>
                  </div>
                  <div v-else-if="row.internalNumber" class="sc__consolidado">
                    Trámite {{ row.internalNumber }}
                  </div>
                </td>
                <!-- # Control = número de la DECLARACIÓN. Se edita aquí y se
                   guarda en el trámite (la fila padre en un consolidado). -->
                <td class="sc__control" @click.stop>
                  <input
                    type="text"
                    class="sc__input sc__control-input"
                    placeholder="N° de declaración"
                    :value="editorValue(row, 'controlNumber')"
                    :readonly="editorOf(row.invoiceId, 'controlNumber').saving"
                    :class="{
                      'sc__control-input--error': editorOf(
                        row.invoiceId,
                        'controlNumber'
                      ).error,
                    }"
                    aria-label="Número de declaración"
                    @input="
                      onEditorInput(row, 'controlNumber', $event.target.value)
                    "
                    @keyup.enter="$event.target.blur()"
                    @keyup.esc="cancelEditor(row, 'controlNumber', $event)"
                    @blur="saveEditor(row, 'controlNumber')"
                  />
                  <span
                    v-if="editorOf(row.invoiceId, 'controlNumber').saving"
                    class="sc__control-status"
                  >
                    Guardando…
                  </span>
                  <span
                    v-else-if="editorOf(row.invoiceId, 'controlNumber').error"
                    class="sc__control-error"
                  >
                    {{ editorOf(row.invoiceId, 'controlNumber').error }}
                  </span>
                </td>
                <!-- N° de la factura Alegra del trámite (la de reembolso o la de
                   servicios, según el tipo). Es obligatorio: sin él no se
                   emite. Se guarda en el trámite al salir del campo. -->
                <td class="sc__control" @click.stop>
                  <input
                    type="text"
                    class="sc__input sc__control-input"
                    placeholder="N° factura Alegra"
                    :value="editorValue(row, 'alegraNumber')"
                    :readonly="editorOf(row.invoiceId, 'alegraNumber').saving"
                    :class="{
                      'sc__control-input--error': editorOf(
                        row.invoiceId,
                        'alegraNumber'
                      ).error,
                      'sc__control-input--missing': !row.alegraNumber,
                    }"
                    aria-label="Número de factura Alegra"
                    @input="
                      onEditorInput(row, 'alegraNumber', $event.target.value)
                    "
                    @keyup.enter="$event.target.blur()"
                    @keyup.esc="cancelEditor(row, 'alegraNumber', $event)"
                    @blur="saveEditor(row, 'alegraNumber')"
                  />
                  <span
                    v-if="editorOf(row.invoiceId, 'alegraNumber').saving"
                    class="sc__control-status"
                  >
                    Guardando…
                  </span>
                  <span
                    v-else-if="editorOf(row.invoiceId, 'alegraNumber').error"
                    class="sc__control-error"
                  >
                    {{ editorOf(row.invoiceId, 'alegraNumber').error }}
                  </span>
                  <span v-else-if="!row.alegraNumber" class="sc__control-error">
                    Obligatorio para emitir
                  </span>
                  <!-- Avisa, no bloquea: una misma factura de Alegra podría
                       cubrir dos trámites, así que la decisión es de
                       contabilidad. -->
                  <span
                    v-else-if="alegraWarn(row)"
                    class="sc__control-warn"
                    :title="alegraWarn(row)"
                  >
                    {{ alegraWarn(row) }}
                  </span>
                </td>
                <!-- La fecha la pone el adjunto; aquí solo se corrige si la
                   factura se generó otro día. Es la que define el mes. -->
                <td class="sc__control" @click.stop>
                  <template v-if="row.alegraDate">
                    <input
                      type="date"
                      class="sc__input sc__control-input sc__control-input--date"
                      :value="editorValue(row, 'alegraDate')"
                      :readonly="editorOf(row.invoiceId, 'alegraDate').saving"
                      :class="{
                        'sc__control-input--error': editorOf(
                          row.invoiceId,
                          'alegraDate'
                        ).error,
                      }"
                      aria-label="Fecha de la factura Alegra"
                      @input="
                        onEditorInput(row, 'alegraDate', $event.target.value)
                      "
                      @keyup.enter="$event.target.blur()"
                      @keyup.esc="cancelEditor(row, 'alegraDate', $event)"
                      @blur="saveEditor(row, 'alegraDate')"
                    />
                    <span
                      v-if="editorOf(row.invoiceId, 'alegraDate').saving"
                      class="sc__control-status"
                    >
                      Guardando…
                    </span>
                    <span
                      v-else-if="editorOf(row.invoiceId, 'alegraDate').error"
                      class="sc__control-error"
                    >
                      {{ editorOf(row.invoiceId, 'alegraDate').error }}
                    </span>
                  </template>
                  <span v-else class="sc__muted">Sin factura adjunta</span>
                </td>
                <td class="sc__right">{{ money(row.cif) }}</td>
                <template v-if="isReembolso">
                  <td class="sc__right">{{ cell(row.TAX) }}</td>
                  <td class="sc__right">{{ cell(row.REIMBURSEMENT) }}</td>
                </template>
                <td v-else class="sc__right">{{ cell(row.SERVICE) }}</td>
                <td class="sc__right sc__amount sc__sticky">
                  {{ money(row.subtotal) }}
                </td>
              </tr>
              <tr v-if="porTramite.length === 0">
                <td :colspan="tableCols" class="sc__empty">
                  No hay {{ kindPlural }} que cobrar
                  <template v-if="period"
                    >en {{ periodLabel }}</template
                  >.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="porTramite.length" class="sc__help sc__help--control">
          # Control: número de la DECLARACIÓN. N° Alegra: el de la factura
          {{ alegraDe }} del trámite. Se guardan en el trámite al salir del
          campo.
        </p>
        <!-- Una fecha corregida a otro mes saca la línea de este estado de
             cuenta: se dice cuál, para que no parezca que desapareció. -->
        <p v-if="notice" class="sc__notice">{{ notice }}</p>
        <!-- Lo que impide emitir, dicho antes de intentarlo. -->
        <p v-if="porTramite.length && !period" class="sc__warn">
          Elige un mes en la pantalla para poder emitir: el estado de cuenta es
          mensual.
        </p>
        <p v-else-if="faltanAlegra" class="sc__warn">
          Faltan N° de factura Alegra en {{ faltanAlegra }}
          {{ faltanAlegra === 1 ? 'trámite' : 'trámites' }}: escríbelos en la
          columna N° Alegra para poder emitir.
        </p>
      </template>

      <div class="sc__field">
        <label class="sc__label">Nota para el cliente</label>
        <textarea
          v-model="form.notes"
          rows="2"
          class="sc__input sc__textarea"
          placeholder="Opcional. Sale en el correo del estado de cuenta."
        ></textarea>
      </div>

      <p v-if="error" class="sc__error">{{ error }}</p>
    </div>

    <footer class="sc__foot">
      <button class="sc__btn sc__btn--ghost" @click="$emit('close-create')">
        Cancelar
      </button>
      <button class="sc__btn sc__btn--save" :disabled="!canEmit" @click="save">
        <v-icon small class="sc__btn-icon">post_add</v-icon>
        {{ saving ? 'Emitiendo…' : 'Emitir estado de cuenta' }}
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment'

// Sin depender del locale de moment: la app no lo fija.
const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

// Campo del trámite que guarda cada editor de la fila, según el tipo de
// estado de cuenta: la factura Alegra de reembolso o la de servicios.
const FIELD_BY_KIND = {
  controlNumber: { REEMBOLSO: 'controlNumber', SERVICIOS: 'controlNumber' },
  alegraNumber: {
    REEMBOLSO: 'reimbursementInvoiceNumber',
    SERVICIOS: 'serviceInvoiceNumber',
  },
  alegraDate: {
    REEMBOLSO: 'reimbursementInvoiceDate',
    SERVICIOS: 'serviceInvoiceDate',
  },
}

const PREVIEW_ERROR = 'No se pudo calcular lo pendiente de este cliente.'

const FIELD_LABEL = {
  controlNumber: 'el # Control',
  alegraNumber: 'el N° Alegra',
  alegraDate: 'la fecha Alegra',
}

export default {
  props: {
    client: {
      type: Object,
      required: true,
    },
    // REEMBOLSO (impuesto adelantado + pagos por cuenta del cliente) o
    // SERVICIOS (facturación por servicios): nunca los dos juntos.
    kind: {
      type: String,
      default: 'REEMBOLSO',
    },
    // Mes de la factura Alegra (AAAA-MM). Obligatorio para emitir; vacío
    // solo sirve para revisar todo lo pendiente.
    period: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    items: [],
    loading: true,
    // Recálculo en segundo plano tras corregir una fecha: la tabla sigue a
    // la vista mientras tanto.
    refreshing: false,
    refreshAgain: false,
    previewSeq: 0,
    notice: '',
    saving: false,
    error: '',
    form: { dueDate: '', notes: '' },
    // Edición en línea por trámite y campo (# Control, N° Alegra, fecha):
    // { [`${invoiceId}:${field}`]: { draft, saving, error } }.
    editors: {},
    // Aviso de N° de factura Alegra repetido, por trámite: { [invoiceId]: texto }.
    alegraDupes: {},
  }),
  computed: {
    isReembolso() {
      return this.kind === 'REEMBOLSO'
    },
    // El nombre del documento, igual que en la pantalla, el correo y el
    // Excel: "Reembolso de gastos operativos" / "Servicios aduanales". En
    // estos textos va dentro de una frase, así que se usa en minúscula.
    kindFrase() {
      return this.isReembolso
        ? 'reembolso de gastos operativos'
        : 'servicios aduanales'
    },
    kindPlural() {
      return this.isReembolso
        ? 'reembolsos de gastos operativos'
        : 'servicios aduanales'
    },
    // La factura Alegra que respalda cada tipo, con el mismo nombre.
    alegraDe() {
      return this.isReembolso
        ? 'de reembolso de gastos operativos'
        : 'de servicios aduanales'
    },
    periodLabel() {
      return this.period
        ? this.mesDe(this.period)
        : 'Todos los meses pendientes'
    },
    tableCols() {
      return this.isReembolso ? 8 : 7
    },
    // Trámites a los que aún les falta el N° de factura Alegra.
    faltanAlegra() {
      return this.porTramite.filter(r => !r.alegraNumber).length
    },
    canEmit() {
      return (
        !this.saving &&
        !this.loading &&
        !this.refreshing &&
        !!this.period &&
        this.porTramite.length > 0 &&
        this.faltanAlegra === 0
      )
    },
    // Un renglón por concepto llega del servidor; la tabla lo lee por trámite.
    porTramite() {
      const map = new Map()
      this.items.forEach(it => {
        if (!map.has(it.invoiceId)) {
          map.set(it.invoiceId, {
            invoiceId: it.invoiceId,
            invoiceNumber: it.invoiceNumber,
            // Los números del cliente vienen del servidor; si la API es la
            // anterior y no los manda, se cae al número del trámite.
            clientInvoiceNumbers:
              it.clientInvoiceNumbers && it.clientInvoiceNumbers.length
                ? it.clientInvoiceNumbers
                : [it.invoiceNumber],
            consolidated: !!(it.consolidated || it.consolidationNumber),
            consolidationNumber: it.consolidationNumber || null,
            fdcontrolNumber: it.fdcontrolNumber || null,
            internalNumber: it.internalNumber || null,
            controlNumber: it.controlNumber,
            // La factura Alegra del trámite para este tipo (todos los
            // conceptos de un trámite comparten la misma).
            alegraNumber: it.alegraNumber || null,
            alegraDate: it.alegraDate || null,
            cif: it.cif,
            TAX: 0,
            REIMBURSEMENT: 0,
            SERVICE: 0,
            subtotal: 0,
          })
        }
        const row = map.get(it.invoiceId)
        row[it.concept] += it.amount
        row.subtotal = this.round(row.subtotal + it.amount)
      })
      return [...map.values()]
    },
    totals() {
      const acc = { TAX: 0, REIMBURSEMENT: 0, SERVICE: 0 }
      this.items.forEach(it => {
        acc[it.concept] = this.round(acc[it.concept] + it.amount)
      })
      return acc
    },
    counts() {
      const acc = { TAX: 0, REIMBURSEMENT: 0, SERVICE: 0 }
      this.items.forEach(it => {
        acc[it.concept] += 1
      })
      return acc
    },
    total() {
      return this.round(
        this.totals.TAX + this.totals.REIMBURSEMENT + this.totals.SERVICE
      )
    },
    tramites() {
      return this.porTramite.length
    },
  },
  created() {
    // El vencimiento a 30 días es lo habitual; contabilidad lo cambia si no.
    this.form.dueDate = moment()
      .add(30, 'days')
      .format('YYYY-MM-DD')
    this.preview()
  },
  methods: {
    round(n) {
      return Math.round((n + Number.EPSILON) * 100) / 100
    },
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    cell(value) {
      return value ? this.money(value) : '—'
    },
    // El consolidado se nombra por su CONS- (internalNumber del padre). El
    // control F&D viejo (LN…) ya no es su número: va aparte, como en las
    // bandas de Transit (mixin TramiteBand).
    consolidadoDe(row) {
      return (
        row.internalNumber ||
        row.consolidationNumber ||
        row.fdcontrolNumber ||
        row.invoiceNumber
      )
    },
    controlFdDe(row) {
      if (!row.consolidated || !row.fdcontrolNumber) return null
      return row.fdcontrolNumber === this.consolidadoDe(row)
        ? null
        : row.fdcontrolNumber
    },
    // "Septiembre 2026" a partir de AAAA-MM.
    mesDe(period) {
      const m = /^(\d{4})-(\d{2})$/.exec(period || '')
      if (!m) return ''
      return `${MESES[parseInt(m[2], 10) - 1]} ${m[1]}`
    },
    // ---- Editores en línea por trámite: # Control, N° Alegra y fecha ----
    editorKey(invoiceId, field) {
      return `${invoiceId}:${field}`
    },
    editorOf(invoiceId, field) {
      return (
        this.editors[this.editorKey(invoiceId, field)] || {
          draft: null,
          saving: false,
          error: '',
        }
      )
    },
    setEditor(invoiceId, field, state) {
      this.$set(this.editors, this.editorKey(invoiceId, field), state)
    },
    editorValue(row, field) {
      const st = this.editors[this.editorKey(row.invoiceId, field)]
      return st && st.draft !== null ? st.draft : row[field] || ''
    },
    onEditorInput(row, field, value) {
      this.setEditor(row.invoiceId, field, {
        ...this.editorOf(row.invoiceId, field),
        draft: value,
        error: '',
      })
      // El aviso de repetido es del número guardado: mientras se teclea otro
      // ya no aplica y se recalcula al salir del campo.
      if (field === 'alegraNumber') this.setAlegraWarn(row.invoiceId, '')
    },
    // Escape descarta lo escrito y suelta el foco; el blur que sigue no
    // guarda nada porque ya no hay borrador.
    cancelEditor(row, field, event) {
      this.setEditor(row.invoiceId, field, {
        draft: null,
        saving: false,
        error: '',
      })
      event.target.value = row[field] || ''
      event.target.blur()
    },
    // Guarda en el TRÁMITE (la fila padre en un consolidado): row.invoiceId es
    // el id del trámite que devuelve el servidor en los ítems pendientes. El
    // campo real depende del tipo: N° y fecha de la factura Alegra de
    // reembolso o de la de servicios.
    saveEditor(row, field) {
      const st = this.editorOf(row.invoiceId, field)
      if (st.draft === null || st.saving) return
      const next = String(st.draft).trim()
      const current = row[field] || ''
      if (next === current) {
        this.setEditor(row.invoiceId, field, {
          draft: null,
          saving: false,
          error: '',
        })
        // Salir del campo sin cambiarlo también revisa si está repetido: el
        // aviso se limpió al teclear y hay que devolverlo si sigue vigente.
        if (field === 'alegraNumber') this.checkAlegraDupes(row, current)
        return
      }
      // La fecha la crea el adjunto; aquí solo se corrige, nunca se borra.
      if (field === 'alegraDate' && next === '') {
        this.setEditor(row.invoiceId, field, {
          draft: next,
          saving: false,
          error: 'La fecha no puede quedar vacía.',
        })
        return
      }
      const column = FIELD_BY_KIND[field][this.kind]
      const fallback = `No se pudo guardar ${FIELD_LABEL[field]}.`
      this.setEditor(row.invoiceId, field, {
        draft: next,
        saving: true,
        error: '',
      })
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: row.invoiceId,
          [column]: next,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.setEditor(row.invoiceId, field, {
              draft: next,
              saving: false,
              error: this.errorText(response, fallback),
            })
            return
          }
          // La tabla se arma desde items: se actualizan sus renglones (el
          // alias y la columna real) para que el valor sobreviva al
          // recálculo del computed.
          const value = next === '' ? null : next
          this.items
            .filter(it => it.invoiceId === row.invoiceId)
            .forEach(it => {
              this.$set(it, field, value)
              this.$set(it, column, value)
            })
          this.setEditor(row.invoiceId, field, {
            draft: null,
            saving: false,
            error: '',
          })
          // La fecha decide el mes: si la línea se fue a otro, el emisor ya
          // no la toma y la vista previa tampoco debe (ni en los totales).
          if (field === 'alegraNumber') {
            this.checkAlegraDupes(row, value)
          }
          if (field === 'alegraDate') {
            this.announceMove(row, value)
            this.preview(true)
          } else if (this.refreshing) {
            // Un recálculo que salió antes de este guardado traería el valor
            // viejo: se repite al terminar.
            this.refreshAgain = true
          }
        })
        .catch(() => {
          this.setEditor(row.invoiceId, field, {
            draft: next,
            saving: false,
            error: fallback,
          })
        })
    },
    // ---- Aviso de N° de factura Alegra repetido ----
    alegraWarn(row) {
      return this.alegraDupes[row.invoiceId] || ''
    },
    setAlegraWarn(invoiceId, text) {
      this.$set(this.alegraDupes, invoiceId, text)
    },
    // Los trámites se nombran por su consecutivo (CONS-/IND-), que es como se
    // leen en el resto de las pantallas.
    dupeText(duplicates) {
      const nombres = (duplicates || [])
        .map(d => d.internalNumber || d.invoiceNumber)
        .filter(Boolean)
      if (!nombres.length) return ''
      const visibles = nombres.slice(0, 2).join(' y ')
      const resto = nombres.length - 2
      return resto > 0
        ? `Este N° ya está en ${visibles} y ${resto} más`
        : `Este N° ya está en ${visibles}`
    },
    checkAlegraDupes(row, value) {
      const numero = String(value || '').trim()
      if (!numero) {
        this.setAlegraWarn(row.invoiceId, '')
        return
      }
      this.$store
        .dispatch('invoices/checkAlegraNumber', {
          field: FIELD_BY_KIND.alegraNumber[this.kind],
          value: numero,
          invoiceId: row.invoiceId,
        })
        .then(duplicates => {
          this.setAlegraWarn(row.invoiceId, this.dupeText(duplicates))
        })
        .catch(() => {
          // Si la consulta falla no se inventa un aviso: el número es válido
          // igual y emitir no depende de esto.
          this.setAlegraWarn(row.invoiceId, '')
        })
    },
    announceMove(row, date) {
      const mes = String(date || '').slice(0, 7)
      if (!this.period || !mes || mes === this.period) return
      const tramite = row.consolidated
        ? this.consolidadoDe(row)
        : row.internalNumber || row.invoiceNumber
      this.notice = `El trámite ${tramite} pasó a ${this.mesDe(
        mes
      )}: sale de este estado de cuenta.`
    },
    // El backend responde { error: 'mensaje' }; se cubre también la forma
    // anidada por si cambia.
    errorText(response, fallback) {
      const data = response && response.data
      const err = data && (data.error || data.message)
      if (typeof err === 'string' && err) return err
      if (err && typeof err.error === 'string' && err.error) return err.error
      return fallback
    },
    // quiet: recalcula sin tapar la tabla (tras corregir una fecha). Solo
    // vale la última respuesta, por si se corrigen varias seguidas.
    preview(quiet = false) {
      const seq = ++this.previewSeq
      if (quiet) {
        this.refreshing = true
      } else {
        this.loading = true
        this.error = ''
      }
      this.$store
        .dispatch('accountStatement/getPendingItems', {
          clientType: this.client.clientType,
          clientId: this.client.clientId,
          kind: this.kind,
          // Sin mes no se manda la clave: el query string serializaba
          // `undefined` como texto.
          ...(this.period ? { period: this.period } : {}),
        })
        .then(items => {
          if (seq !== this.previewSeq) return
          if (items === null) {
            this.error = PREVIEW_ERROR
            return
          }
          if (this.error === PREVIEW_ERROR) this.error = ''
          this.items = items
        })
        .finally(() => {
          if (seq !== this.previewSeq) return
          this.loading = false
          this.refreshing = false
          if (this.refreshAgain) {
            this.refreshAgain = false
            this.preview(true)
          }
        })
    },
    save() {
      if (!this.canEmit) return
      this.error = ''
      this.saving = true
      this.$store
        .dispatch('accountStatement/create', {
          clientType: this.client.clientType,
          clientId: this.client.clientId,
          kind: this.kind,
          period: this.period,
          dueDate: this.form.dueDate || undefined,
          notes: this.form.notes || undefined,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = this.errorText(
              response,
              'No se pudo emitir el estado de cuenta.'
            )
            return
          }
          this.$emit('created', response)
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.sc {
  border-radius: 10px;
  overflow: hidden;
}

.sc__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  background: #254f87;
}
.sc__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a8c4e6;
}
.sc__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.sc__scope {
  margin: 6px 0 0;
  font-size: 13px;
  color: #dbe7f5;
}
.sc__close {
  flex: none;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.14);
  cursor: pointer;
}
.sc__close .v-icon {
  color: #ffffff !important;
  font-size: 16px !important;
}
.sc__close:hover {
  background: rgba(255, 255, 255, 0.26);
}

.sc__body {
  padding: 20px 22px;
  max-height: 62vh;
  overflow-y: auto;
  /* Lo ancho se desplaza dentro de su tabla, nunca el diálogo entero. */
  overflow-x: hidden;
}

.sc__inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}
.sc__field {
  flex: 1 1 150px;
  min-width: 0;
  margin-bottom: 12px;
}
.sc__field--short {
  flex: 0 1 220px;
}
.sc__label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.sc__input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #1e293b;
  font-size: 13px;
  outline: none;
}
.sc__input:focus {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.14);
}
.sc__textarea {
  height: auto;
  padding: 8px 10px;
  resize: vertical;
  font-family: inherit;
}
.sc__help {
  margin: 0 0 16px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.sc__totals {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.sc__total {
  flex: 1 1 150px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.sc__total--grand {
  border-color: #254f87;
  background: #eef2f7;
}
.sc__total-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sc__total-value {
  display: block;
  margin-top: 4px;
  font-size: 17px;
  color: #1e293b;
}
.sc__total--grand .sc__total-value {
  color: #254f87;
}
.sc__total-count {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #94a3b8;
}
.sc__total-hint {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #94a3b8;
}

.sc__table-wrap {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 16px;
  /* La barra se dibuja siempre: en Mac se esconde sola y la tabla parecía
     cortada. */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}
.sc__table-wrap::-webkit-scrollbar {
  height: 8px;
}
.sc__table-wrap::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.sc__table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.sc__table-wrap::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
/* Bordes separados: con collapse, el borde de una celda fija se queda atrás
   al desplazar. */
.sc__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.sc__col-factura {
  min-width: 150px;
}
/* El Subtotal queda fijo a la derecha, con fondo propio y una sombra suave
   que marca lo que pasa por debajo. */
.sc__sticky {
  position: sticky;
  right: 0;
  z-index: 1;
}
.sc__table th.sc__sticky {
  background: #ffffff;
}
.sc__sticky::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8px;
  width: 8px;
  background: linear-gradient(
    to left,
    rgba(15, 23, 42, 0.07),
    rgba(15, 23, 42, 0)
  );
  pointer-events: none;
}
.sc__table th {
  padding: 8px 10px;
  border-bottom: 2px solid #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  white-space: nowrap;
}
.sc__table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}
.sc__right {
  text-align: right;
}
.sc__strong {
  font-weight: 600;
  color: #1e293b;
}
.sc__factura {
  line-height: 1.35;
  white-space: nowrap;
}
.sc__consolidado {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  color: #64748b;
  /* Con el control F&D la referencia se alarga: baja de línea en vez de
     ensanchar la columna. */
  white-space: normal;
}
/* # Control editable en la fila. */
.sc__control {
  vertical-align: top;
}
.sc__control-input {
  width: 124px;
  height: 30px;
  padding: 0 8px;
  font-variant-numeric: tabular-nums;
}
.sc__control-input--error {
  border-color: #dc2626;
}
/* Sin N° Alegra no se emite: el vacío se ve desde antes de intentarlo. */
.sc__control-input--missing {
  border-style: dashed;
  border-color: #dc2626;
  background: rgba(220, 38, 38, 0.04);
}
.sc__control-input--date {
  width: 140px;
}
.sc__muted {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}
.sc__control-status,
.sc__control-error,
.sc__control-warn {
  display: block;
  margin-top: 3px;
  max-width: 140px;
  font-size: 11px;
  line-height: 1.3;
  white-space: normal;
}
.sc__control-status {
  color: #64748b;
}
.sc__control-error {
  color: #b91c1c;
}
/* Ámbar: un N° repetido puede ser correcto, así que avisa sin alarmar. */
.sc__control-warn {
  color: #b45309;
}
.sc__help--control {
  margin: 8px 0 0;
}
.sc__amount {
  font-weight: 700;
  color: #1e293b;
  background: #f8fafc;
}
.sc__empty {
  padding: 22px 10px !important;
  text-align: center;
  color: #94a3b8;
}

.sc__notice {
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #eef2f7;
  color: #254f87;
  font-size: 12px;
  line-height: 1.5;
}

.sc__warn {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #b91c1c;
  line-height: 1.5;
}

.sc__error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(220, 38, 38, 0.1);
  color: #991b1b;
  font-size: 13px;
}

.sc__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.sc__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.sc__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sc__btn-icon {
  font-size: 16px !important;
}
.sc__btn--ghost {
  border-color: #cbd5e1;
  background: #ffffff;
  color: #475569;
}
.sc__btn--ghost:hover {
  background: #f1f5f9;
}
.sc__btn--save {
  background: #254f87;
  color: #ffffff;
}
.sc__btn--save .v-icon {
  color: #ffffff !important;
}
.sc__btn--save:hover:not(:disabled) {
  background: #1e4270;
}
</style>
